// Define the structure for WebSocket messages
export interface WebSocketMessage {
  type: string
  data?: any
  correlationId?: string
  error?: string
}

// Define the structure for Dashboard Request
export interface DashboardRequest {
  graphName?: string
  cimCodeIds?: string[]
  deviceId: string
  siteId?: string
  isDefault?: boolean
  tenantId: string
  username?: string
  additionalParams?: Record<string, string>
}

// Define the structure for Asset Dashboard Request
export interface AssetDashboardRequest {
  graphName?: string
  cimCodeIds?: string[]
  siteId?: string
  isDefault?: boolean
  tenantId: string
  username?: string
  devices?: object
  assets?: object
}

// Define the structure for Asset Readings
export interface AssetReadingsDTO {
  readingTime: string
  value: number
  indexValue: number
}

// Define the structure for Thermal Device Data
export interface ThermalDeviceData {
  deviceId: string
  gatewayId: string
  isError: boolean
  errorMessage?: string
  isGatewayOnline: boolean
}

// Define the structure for Asset Data
export interface AssetDataDTO {
  assetId: string
  multiplier: number
  intervalLength: number
  lastReadingTime: string
  cimCodeId: string
  tenantId: string
  channelName?: string
  assetReadings: AssetReadingsDTO[]
  thermalDeviceData?: ThermalDeviceData
}

// Define WebSocket connection status
export enum WebSocketStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error'
}

// Define the structure for Alarm Data from WebSocket
export interface AlarmWebSocketData {
  tenantId: string
  alarmLabelId: string
  alarmLowerLimitLabelName: string
  urgentAlerts: number
  highAlerts: number
  lowAlerts: number
  assetIds: string[]
  cimCodeIds: string[]
  assetTypeIds: string[]
  deviceIds: string[]
}

// Define the structure for Register Reading Data from WebSocket
export interface RegisterReading {
  readingDateTime: string
  readingTime: number[]
  value: number
  indexValue: number
  registerName: string
  units: string
  formattedValue: string | null
  cimCodeId: string
  colorValue: string
  execution_status?: string
  parameterName?: string
}

// Define the structure for KPI Data from WebSocket
export interface KPIData {
  kpi_definition_id: string
  timestamp: string
  kpi_name: string
  kpi_value: number
  execution_status: string
  is_custom_kpi: boolean
  units: string
  colorValue: string
  formattedValue: string | null
  kpiName?: string // Alternative property name
  value?: number // Alternative property name
}

// Define the structure for Dashboard Modbus Data Update
export interface DashboardModbusDataUpdate {
  registerReadings: RegisterReading[]
  asset_id: string
  tenant_id: string
}

// Define the structure for Dashboard KPI Data Update
export interface DashboardKPIDataUpdate {
  asset_id: string
  tenant_id: string
  kpi_data: KPIData[]
}

// Define the structure for Dashboard Device Status Update
export interface DashboardDeviceStatusUpdate {
  asset_id: string
  tenant_id: string
  is_asset_online: boolean
  communication_time: string
  communication_status: string
}

// Define the structure for WebSocket Message Types
export type WebSocketMessageType =
  | 'DASHBOARD_MODBUS_DATA_UPDATE'
  | 'DASHBOARD_KPI_DATA_UPDATE'
  | 'DASHBOARD_DEVICE_STATUS_UPDATE'
  | 'HEARTBEAT'
  | 'SUBSCRIPTION_CONFIRMED'
  | 'ERROR'

// Define the structure for typed WebSocket messages
export interface TypedWebSocketMessage<T = any> {
  type: WebSocketMessageType
  data: T
  correlationId?: string
  error?: string
}

// Specific message types
export type ModbusDataUpdateMessage = TypedWebSocketMessage<DashboardModbusDataUpdate>
export type KPIDataUpdateMessage = TypedWebSocketMessage<DashboardKPIDataUpdate>
